/**
 * UC-176 / UC-177 — client helpers for variant row association RPCs.
 */

import { toMatchKey } from '@/shared/variantMapping/matchKey';

export const ASSOCIATE_MENU_LABEL = 'Associate another Variant with this row';
export const ASSOCIATE_MODAL_TITLE = 'Associate this row with another';
export const ASSOCIATE_SUCCESS_MESSAGE = 'Variants row merged successfully.';
export const ASSOCIATE_NOT_FOUND_MESSAGE = 'No variant matches that value in this merchant.';
export const ASSOCIATE_SAME_ROW_MESSAGE = 'That value already belongs to this row.';

export const DISASSOCIATE_MENU_LABEL = 'Disassociate a Variant from this row';
export const DISASSOCIATE_DONE_LABEL = 'Done disassociation';
export const DISASSOCIATE_LAST_IDENTIFIER_MESSAGE =
  'A Variant row must keep at least one identifier.';

export type AssociateVariantResult = {
  ok: boolean;
  code?: string;
  message: string;
  correlation_id?: string;
  survivor_master_product_id?: string;
  source_master_product_id?: string;
};

export type DisassociateVariantResult = {
  ok: boolean;
  code?: string;
  message: string;
  correlation_id?: string;
  new_master_product_id?: string;
};

export interface AssociateLookupRow {
  variantId: string;
  identifiers: Array<{ type: string; value: string }>;
}

/**
 * Pick the identifier-link identity for a pasted value.
 * Key is case-insensitive type + NFC-trimmed value. The same value on two
 * types is two links: when exactly one other row owns that value, that row
 * is the match. Several other rows are left unresolved.
 */
export function resolveAssociateIdentifier(
  rows: AssociateLookupRow[],
  sourceMasterProductId: string,
  rawValue: string,
): { identifierType: string | null; identifierValue: string } | null {
  const identifierValue = toMatchKey(rawValue);
  if (!identifierValue) return null;

  const hits: Array<{ variantId: string; type: string }> = [];
  for (const row of rows) {
    for (const identifier of row.identifiers) {
      const type = identifier.type.trim();
      if (!type) continue;
      if (toMatchKey(identifier.value) !== identifierValue) continue;
      hits.push({ variantId: row.variantId, type });
    }
  }

  const otherMasterIds = new Set(
    hits.filter((hit) => hit.variantId !== sourceMasterProductId).map((hit) => hit.variantId),
  );

  if (otherMasterIds.size === 1) {
    const match = hits.find((hit) => hit.variantId !== sourceMasterProductId);
    return { identifierType: match?.type ?? null, identifierValue };
  }

  if (otherMasterIds.size === 0 && hits.length > 0) {
    return { identifierType: hits[0].type, identifierValue };
  }

  return { identifierType: null, identifierValue };
}

type RpcClient = {
  rpc: (
    fn: string,
    args: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
};

function asResultObject(data: unknown): Record<string, unknown> | null {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

export async function associateVariantRows(
  client: RpcClient,
  args: {
    organisationId: string;
    sourceMasterProductId: string;
    identifierValue: string;
    identifierType?: string | null;
    actor?: string;
  },
): Promise<AssociateVariantResult> {
  const { data, error } = await client.rpc('associate_variant_rows_v1', {
    p_org: args.organisationId,
    p_source_master_product_id: args.sourceMasterProductId,
    p_identifier_value: args.identifierValue,
    p_identifier_type: args.identifierType ?? null,
    p_actor: args.actor ?? null,
  });

  if (error) {
    return { ok: false, code: 'rpc_error', message: error.message };
  }

  const body = asResultObject(data);
  if (!body) {
    return { ok: false, code: 'invalid_response', message: ASSOCIATE_NOT_FOUND_MESSAGE };
  }

  const ok = body.ok === true;
  return {
    ok,
    code: typeof body.code === 'string' ? body.code : undefined,
    message:
      typeof body.message === 'string'
        ? body.message
        : ok
          ? ASSOCIATE_SUCCESS_MESSAGE
          : ASSOCIATE_NOT_FOUND_MESSAGE,
    correlation_id: typeof body.correlation_id === 'string' ? body.correlation_id : undefined,
    survivor_master_product_id:
      typeof body.survivor_master_product_id === 'string'
        ? body.survivor_master_product_id
        : undefined,
    source_master_product_id:
      typeof body.source_master_product_id === 'string'
        ? body.source_master_product_id
        : undefined,
  };
}

export async function disassociateVariantIdentifier(
  client: RpcClient,
  args: {
    organisationId: string;
    sourceMasterProductId: string;
    linkId: string;
    actor?: string;
  },
): Promise<DisassociateVariantResult> {
  const { data, error } = await client.rpc('disassociate_variant_identifier_v1', {
    p_org: args.organisationId,
    p_source_master_product_id: args.sourceMasterProductId,
    p_link_id: args.linkId,
    p_actor: args.actor ?? null,
  });

  if (error) {
    return { ok: false, code: 'rpc_error', message: error.message };
  }

  const body = asResultObject(data);
  if (!body) {
    return { ok: false, code: 'invalid_response', message: 'Unable to disassociate that identifier.' };
  }

  const ok = body.ok === true;
  return {
    ok,
    code: typeof body.code === 'string' ? body.code : undefined,
    message:
      typeof body.message === 'string'
        ? body.message
        : ok
          ? 'Identifier disassociated.'
          : DISASSOCIATE_LAST_IDENTIFIER_MESSAGE,
    correlation_id: typeof body.correlation_id === 'string' ? body.correlation_id : undefined,
    new_master_product_id:
      typeof body.new_master_product_id === 'string' ? body.new_master_product_id : undefined,
  };
}
